/**
 * Buy Now: POST /cart/add.js (same contract as product-form.js), then redirect to checkout.
 */

const PROPERTY_FIELD_SELECTOR =
  'input[name^="properties["],textarea[name^="properties["],select[name^="properties["]';
/** Only allow Shopify line-item property keys (blocks arbitrary FormData keys). */
const PROPERTY_NAME_PATTERN = /^properties\[[^\]]+]$/;
const MAX_PROPERTY_VALUE_CHARS = 5000;
const MAX_QUANTITY = 5000;

function getCartAddUrl() {
  return typeof window.routes !== 'undefined' && window.routes.cart_add_url
    ? window.routes.cart_add_url
    : '/cart/add.js';
}

function parseCartAddBody(text) {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid cart response. Try again or use Add to cart.');
  }
}

/**
 * @param {Response} response
 * @returns {Promise<object>}
 */
async function readCartAddResult(response) {
  const data = parseCartAddBody(await response.text());
  if (!response.ok) {
    throw new Error(data.description || data.message || `Cart error (${response.status})`);
  }
  if (data.status) {
    const err =
      data.description ||
      data.message ||
      (typeof data.errors === 'string'
        ? data.errors
        : data.errors
          ? JSON.stringify(data.errors)
          : 'Failed to add to cart');
    throw new Error(err);
  }
  return data;
}

function isValidVariantId(value) {
  return typeof value === 'string' && /^\d+$/.test(value.trim());
}

function clampQuantity(raw) {
  const n = parseInt(raw, 10);
  if (Number.isNaN(n) || n < 1) return 1;
  return Math.min(n, MAX_QUANTITY);
}

/**
 * @param {FormData} fd
 * @param {string} name
 * @param {string} value
 */
function appendSafeProperty(fd, name, value) {
  if (!PROPERTY_NAME_PATTERN.test(name)) return;
  const v = String(value).slice(0, MAX_PROPERTY_VALUE_CHARS);
  if (v === '') return;
  fd.append(name, v);
}

class BuyNowButton {
  constructor(form) {
    this.form = form;
    this.button = form.querySelector('button[type="submit"]');
    this.sectionId =
      form.id && typeof form.id === 'string' ? form.id.replace('buy-now-form-', '') : '';
    this.quantityInput = null;
    this.isAdding = false;
    this.init();
  }

  init() {
    this.findQuantityInput();
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  /** Nearest PDP root; avoids section id vs data-section mismatches. */
  getProductInfoRoot() {
    return (
      this.form?.closest?.('product-info') ||
      (this.sectionId
        ? document.querySelector(`product-info[data-section="${this.sectionId}"]`)
        : null)
    );
  }

  findQuantityInput() {
    const pickQtyFromWrapper = (wrapper) => {
      if (!wrapper) return null;
      return (
        wrapper.querySelector('.quantity-input input[name="quantity"]') ||
        wrapper.querySelector('quantity-input input[name="quantity"]')
      );
    };

    const resolveWrapperFromAnchor = (root) => {
      if (!root) return null;
      let wrapper = root.querySelector('.quantity-buy-wrapper');
      if (wrapper) return wrapper;
      if (root.classList?.contains('quantity-buy-wrapper')) return root;
      if (root.id === `Quantity-Form-${this.sectionId}`) {
        return root.closest('.quantity-buy-wrapper');
      }
      if (root.id === `product-form-${this.sectionId}`) {
        return root.closest('product-info')?.querySelector('.quantity-buy-wrapper') ?? null;
      }
      return null;
    };

    const tryScopedAnchors = () => {
      if (!this.sectionId) return null;
      const anchors = [
        document.getElementById(`Quantity-Form-${this.sectionId}`),
        document.getElementById(`product-form-${this.sectionId}`),
      ];
      for (const root of anchors) {
        const fromWrapper = pickQtyFromWrapper(resolveWrapperFromAnchor(root));
        if (fromWrapper) return fromWrapper;
        if (root) {
          const direct =
            root.querySelector('.quantity-input input[name="quantity"]') ||
            root.querySelector('quantity-input input[name="quantity"]');
          if (direct) return direct;
        }
      }
      return null;
    };

    this.quantityInput = tryScopedAnchors();

    if (!this.quantityInput && this.sectionId) {
      const byId = document.getElementById(`product-form-${this.sectionId}`);
      if (byId) {
        this.quantityInput =
          byId.tagName === 'FORM'
            ? byId.querySelector('input[name="quantity"]')
            : byId.querySelector?.('form input[name="quantity"]');
      }
    }

    if (!this.quantityInput) {
      this.quantityInput = document.querySelector('input[name="quantity"]');
    }
  }

  getQuantity() {
    return clampQuantity(this.quantityInput?.value);
  }

  getVariantId() {
    const input = this.form.querySelector('input[name="id"]');
    return input ? String(input.value).trim() : '';
  }

  getViewItem() {
    try {
      const root = this.getProductInfoRoot() || document;
      const el = root.querySelector('[data-dl-view-item]');
      if (!el) return null;
      const data = JSON.parse(el.textContent);
      return data && data.items && data.items[0] ? data.items[0] : null;
    } catch {
      return null;
    }
  }

  getSelectedVariant() {
    try {
      const root = this.getProductInfoRoot() || document;
      const el = root.querySelector('variant-selects [data-selected-variant]');
      return el ? JSON.parse(el.textContent) : null;
    } catch {
      return null;
    }
  }

  /** Line item property fields in this PDP (engraving, gift recipient, etc.). */
  collectPropertyFieldEntries() {
    const root = this.getProductInfoRoot();
    if (!root) return [];

    const seen = new Set();
    const out = [];

    for (const el of root.querySelectorAll(PROPERTY_FIELD_SELECTOR)) {
      if (!el.name || seen.has(el.name) || el.disabled) continue;
      const type = (el.type || '').toLowerCase();
      if ((type === 'checkbox' || type === 'radio') && !el.checked) continue;
      const value = typeof el.value === 'string' ? el.value : String(el.value ?? '');
      if (value === '') continue;
      if (!PROPERTY_NAME_PATTERN.test(el.name)) continue;
      seen.add(el.name);
      out.push({ name: el.name, value });
    }

    return out;
  }

  resetButtonLoading() {
    if (!this.button) return;
    this.button.disabled = false;
    this.button.removeAttribute('data-buy-now-loading');
    this.button.removeAttribute('aria-busy');
    this.button.textContent =
      this.button.getAttribute('data-original-text') || 'Buy Now';
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isAdding) return;

    const engravingRoot = this.getProductInfoRoot();
    if (
      engravingRoot &&
      typeof window.validateCustomEngraving === 'function' &&
      !window.validateCustomEngraving(engravingRoot)
    ) {
      return;
    }

    const variantId = this.getVariantId();
    if (!isValidVariantId(variantId)) {
      alert('Product variant not found. Please refresh the page and try again.');
      return;
    }

    const quantity = this.getQuantity();

    const item = this.getViewItem();
    const variant = this.getSelectedVariant();
    const variantTitle = variant && variant.title !== 'Default Title' ? variant.title : undefined;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'buy_now_button_click',
      location: 'product_page',
      item_id: (variant && variant.sku) || variantId || (item && item.item_id),
      item_name: item ? item.item_name : undefined,
      item_brand: item ? item.item_brand : undefined,
      item_category: item ? item.item_category : undefined,
      item_variant: variantTitle || (item ? item.item_variant : undefined),
      price: variant && variant.price != null ? variant.price / 100 : item ? item.price : undefined,
      quantity,
    });

    if (this.button) {
      this.button.disabled = true;
      this.button.setAttribute('data-buy-now-loading', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.button.setAttribute('data-original-text', this.button.textContent);
      this.button.textContent = 'Adding...';
    }

    const formData = new FormData();
    formData.append('id', variantId.trim());
    formData.append('quantity', String(quantity));
    for (const { name, value } of this.collectPropertyFieldEntries()) {
      appendSafeProperty(formData, name, value);
    }

    this.isAdding = true;

    fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then((r) => readCartAddResult(r))
      .then((data) => {
        // Buy Now never publishes cartUpdate: fire add_to_cart, wait (capped) for the
        // collection-attribute write, then redirect (which always happens).
        const go = () => {
          window.location.href = '/checkout';
        };
        let tracked = null;
        if (typeof window.gtmTrackAddToCart === 'function') {
          try {
            tracked = window.gtmTrackAddToCart(data && data.items ? data.items : data);
          } catch {
            tracked = null;
          }
        }
        Promise.race([
          Promise.resolve(tracked),
          new Promise((resolve) => setTimeout(resolve, 800)),
        ]).then(go, go);
      })
      .catch((error) => {
        this.isAdding = false;
        this.resetButtonLoading();
        alert(`Unable to add product to cart: ${error.message}`);
      });
  }
}

function initBuyNowButtons(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll('form[id^="buy-now-form-"]').forEach((form) => {
    if (form.dataset.buyNowBound === 'true') return;
    form.dataset.buyNowBound = 'true';
    try {
      new BuyNowButton(form);
    } catch {
      /* init skipped */
    }
  });
}

window.initBuyNowButtons = initBuyNowButtons;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBuyNowButtons());
} else {
  initBuyNowButtons();
}
